import format from 'date-fns/format';

import i18n from 'locales/i18n';

import { IGReportRes } from '@apis/orval/model';

import round, { roundFloat } from '@utils/round';

import { KeyMetricsIgProps, PickedCardProps } from './KeyMetrics';

type KeyMetricsIgPropsItemDisabled = PickedCardProps['disabled'];

export class KeyMetricsIgAdapter {
	value: IGReportRes;

	constructor(obj: IGReportRes) {
		this.value = obj;
	}

	get realFollowers() {
		const realFollowersValue = this.value?.core_metrics?.real_follower;
		return realFollowersValue?.toString()
			? i18n?.t('unit.number-of-person', {
					number: round(realFollowersValue, 'down') ?? '',
			  })
			: i18n?.t('unit.number-of-person', { number: '-' });
	}

	get averageReach() {
		const averageReachValue = this.value?.core_metrics?.avg_reach;
		return averageReachValue?.toString()
			? i18n?.t('unit.number-of-person', { number: round(averageReachValue, 'down') })
			: i18n?.t('unit.number-of-person', { number: '-' });
	}

	get averageViews() {
		const averageViewsValue = this.value?.core_metrics?.avg_video_view;
		return averageViewsValue?.toString()
			? i18n?.t('unit.times', { number: round(averageViewsValue, 'down') })
			: i18n?.t('unit.times', { number: '-' });
	}

	get averageLikes() {
		const averageLikesValue = this.value?.core_metrics?.avg_like;
		return averageLikesValue?.toString()
			? i18n?.t('common:unit.counts', { number: round(averageLikesValue, 'down') })
			: i18n?.t('common:unit.counts', { number: '-' });
	}

	get averageComments() {
		const averageCommentsValue = this.value?.core_metrics?.avg_comment;
		return averageCommentsValue?.toString()
			? i18n?.t('common:unit.counts', { number: round(averageCommentsValue, 'down') })
			: i18n?.t('common:unit.counts', { number: '-' });
	}

	get engagementRate() {
		const engagementRateValue = this.value?.core_metrics?.real_engagement;
		return engagementRateValue?.toString()
			? `${roundFloat(engagementRateValue, 'down', 2)} %`
			: '- %';
	}
	get updatedAt() {
		const updatedTime = this.value?.updated_time;

		return updatedTime ? format(new Date(updatedTime), 'yyyy. MM. dd') : undefined;
	}

	get averageLikesDisabled(): KeyMetricsIgPropsItemDisabled {
		const isAvgLikePrivate = this.value?.core_metrics?.is_avg_like_private;
		return isAvgLikePrivate
			? {
					text:
						i18n?.t(
							'infReportInstagram:features.report.adapter.ig.key-metrics.private',
						) ?? '', //'비공개',
					tooltip:
						i18n?.t(
							'infReportInstagram:features.report.adapter.ig.key-metrics.private-tooltip',
						) ?? '', // `분석 대상 콘텐츠 모두 ‘좋아요' 수가 비공개로 설정되어 있어 정확한 평균 수치를 산출할 수 없습니다. 이는 참여율(ER) 등의 지표 분석에 영향을 줄 수 있습니다.`,
			  }
			: undefined;
	}

	get averageCommentsDisabled(): KeyMetricsIgPropsItemDisabled {
		const isAvgCommentPrivate = this.value?.core_metrics?.is_avg_comment_private;
		return isAvgCommentPrivate
			? {
					text:
						i18n?.t(
							'infReportInstagram:features.report.adapter.ig.key-metrics.inactivate',
						) ?? '', //'비활성화',
					tooltip:
						i18n?.t(
							'infReportInstagram:features.report.adapter.ig.key-metrics.inactivate-tooltip',
						) ?? '', //`분석 대상 콘텐츠 모두 댓글 기능이 비활성화되어 있어 댓글 관련 평균 수치를 알 수 없습니다. 이는 참여율(ER) 등의 지표 분석에 영향을 줄 수 있습니다.`,
			  }
			: undefined;
	}

	adapt(): {
		updatedAt?: string;
		props: KeyMetricsIgProps;
	} {
		return {
			updatedAt: this.updatedAt,
			props: {
				realFollowers: { content: this.realFollowers },
				averageReach: { content: this.averageReach },
				averageViews: { content: this.averageViews },
				averageLikes: { content: this.averageLikes, disabled: this.averageLikesDisabled },
				averageComments: {
					content: this.averageComments,
					disabled: this.averageCommentsDisabled,
				},
				engagementRate: { content: this.engagementRate },
			},
		};
	}
}
